import { useTheme } from '@mui/material';
import type Konva from 'konva';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Transformer } from 'react-konva';
import { useStudio, type StudioItem } from '../../context';
import { ALL_ANCHORS, CORNER_ANCHORS, TEXT_ANCHORS, useAnchorStyle } from '../lib';

interface SelectionPreviewEvent {
  ids: Set<string>;
}

interface StudioItemTransformerProps {
  item: StudioItem;
  isSelected: boolean;
  isMultiSelected: boolean;
}

export const StudioItemTransformer = ({
  item,
  isSelected,
  isMultiSelected,
}: StudioItemTransformerProps) => {
  const theme = useTheme();
  const { setTransformer, stage } = useStudio();
  const anchorStyle = useAnchorStyle();
  const transformerRef = useRef<Konva.Transformer>(null);

  const [hovered, setHovered] = useState(false);
  const [selectionPreview, setSelectionPreview] = useState(false);

  const showTransformer = (hovered || isSelected || selectionPreview) && !isMultiSelected;
  const showControls = isSelected && !item.locked;

  const enabledAnchors = useMemo(() => {
    if (!showControls) return [];
    if (item.format === 'svg') return CORNER_ANCHORS;
    if (item.type === 'text') return TEXT_ANCHORS;
    return ALL_ANCHORS;
  }, [item.format, item.type, showControls]);

  const keepRatio = item.format === 'svg';

  // Listen to item's Konva node for hover events
  useEffect(() => {
    if (!stage) return;
    const node = stage.findOne('#' + item.id);
    if (!node) return;

    const onMouseOver = () => setHovered(true);
    const onMouseOut = () => setHovered(false);

    node.on('mouseover', onMouseOver);
    node.on('mouseout', onMouseOut);

    const onSelectionPreview = (event: Konva.KonvaEventObject<SelectionPreviewEvent>) => {
      setSelectionPreview(event.evt.ids.has(item.id));
    };
    stage.on('selectionPreviewChange', onSelectionPreview);

    return () => {
      node.off('mouseover', onMouseOver);
      node.off('mouseout', onMouseOut);
      stage.off('selectionPreviewChange', onSelectionPreview);
    };
  }, [item.id, stage]);

  // Attach item node to transformer
  useEffect(() => {
    const tr = transformerRef.current;
    if (!tr || !stage) return;
    const node = stage.findOne('#' + item.id);
    if (node) {
      tr.nodes([node]);
      tr.getLayer()?.batchDraw();
    }
  }, [item.id, stage, showTransformer]);

  if (!showTransformer) return null;

  return (
    <Transformer
      anchorStroke={theme.palette.primary.main}
      anchorStyleFunc={anchorStyle}
      borderDash={item.locked ? [8, 6] : []}
      borderStroke={theme.palette.primary.main}
      borderStrokeWidth={2}
      draggable={false}
      enabledAnchors={enabledAnchors}
      flipEnabled={false}
      keepRatio={keepRatio}
      rotateEnabled={showControls}
      onTransformStart={() => setTransformer({ transforming: true })}
      onTransformEnd={() => setTransformer({ transforming: false })}
      ref={transformerRef}
      rotationSnaps={[0, 90, 180, 270]}
      rotateAnchorOffset={20}
    />
  );
};
